import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Paper,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CommonButton from "../../../Components/InputComponents/CommonButton";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { axiosAPI } from "../../../Components/AxiosComponents/Axios";
import { decryptId } from "../../../Components/CryptoComponent";

// ----------------- TRANSFORM HELPERS -----------------
const transformProcedureData = (procedure) => {
  return procedure.stages.map((stage) => {
    if (stage.subProcedures?.length > 0) {
      return {
        title: stage.RPS_Name,
        subTabs: stage.subProcedures.map((sub) => ({
          title: sub.RSP_Name,
          fields: sub.fields.map(mapFieldToTableRow),
        })),
      };
    } else {
      return {
        title: stage.RPS_Name,
        fields: stage.fields.map(mapFieldToTableRow),
      };
    }
  });
};

const mapFieldToTableRow = (field) => ({
  name: field.RPF_Name,
  type: field.RPF_Type?.toLowerCase() || "-",
  required: field.RPF_IsRequired === 1 ? "Yes" : "-",
  maxLength: field.RPF_MaxLength || "-",
  expiry: "No", // You can customize this based on your logic
  options: getSelectOptions(field),
});

const getSelectOptions = (field) => {
  if (field.RPF_Type === "Dropdown") {
    try {
      const parsed = JSON.parse(field.RPF_ValidValues || "[]");
      return Array.isArray(parsed) ? parsed.join(", ") : "-";
    } catch {
      return "-";
    }
  }
  return "-";
};

// ----------------- MAIN COMPONENT -----------------
export default function ViewRecruitmentProcedure() {
  const { id } = useParams();
  const decryptedId = decryptId(decodeURIComponent(id));

  const navigate = useNavigate();

  const [procedure, setProcedure] = useState({});
  const [sections, setSections] = useState([]);

  const location = useLocation();
  const [searchParams] = useSearchParams();

  const handleBack = () => {
    const params = new URLSearchParams(location.search);
    const from = params.get("from");

    navigate(from ? `/${from}` : `/RecruitmentProcedure?${searchParams?.toString()}`);
  };

  const getData = () => {
    axiosAPI
      ?.get(`/RecruitmentProcedure/listbyId/${decryptedId}`)
      .then((res) => {
        const data = res?.data?.data;

        if (data) {
          setProcedure(data);

          const transformed = transformProcedureData(data);
          setSections(transformed);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch procedure data:", err);
      });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getData();
  }, []);

  return (
    <Paper sx={{ p: { xs: 2, sm: 3, md: 5 }, m: "auto", minHeight: "100%" }}>
      <CommonButton
        variant="outlined"
        background="#006CB8"
        onClick={handleBack}
        margin="0px 20px 10px 0px"
      >
        <ArrowBackIcon sx={{ color: "white" }} />
      </CommonButton>

      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        mb={2}
      >
        {procedure?.RP_Name}
      </Typography>

      {sections.map((section, i) => (
        <Accordion
          key={i}
          defaultExpanded
          sx={{
            border: "1px solid lightgrey",
            boxShadow: "none",
            margin: "10px auto",
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              sx={{
                fontWeight: 600,
                fontFamily: "Geist, sans-serif",
                fontSize: "20px",
              }}
            >
              {i + 1}) {section.title}
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            {section.subTabs
              ? section.subTabs.map((subTab, j) => (
                  <Box key={j} mb={3} px={2}>
                    <Typography
                      mb={1}
                      sx={{
                        fontWeight: 500,
                        fontFamily: "Geist, sans-serif",
                        fontSize: "18px",
                      }}
                    >
                      <span style={{ fontSize: "17px" }}>
                        {i + 1}.{j + 1})
                      </span>{" "}
                      {subTab.title}
                    </Typography>
                    {renderTable(subTab.fields)}
                  </Box>
                ))
              : renderTable(section.fields)}
          </AccordionDetails>
        </Accordion>
      ))}
    </Paper>
  );
}

// ----------------- RENDER TABLE FUNCTION -----------------
const renderTable = (fields) => {
  if (!fields || fields.length === 0)
    return <Typography>No Fields available</Typography>;

  return (
    <Table size="small" sx={{ mt: 1 }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ minWidth: "150px" }}>
            <strong>Name</strong>
          </TableCell>
          <TableCell sx={{ minWidth: "150px" }}>
            <strong>Type</strong>
          </TableCell>
          <TableCell sx={{ minWidth: "150px" }}>
            <strong>Required</strong>
          </TableCell>
          <TableCell sx={{ minWidth: "150px" }}>
            <strong>Max Length</strong>
          </TableCell>
          <TableCell sx={{ minWidth: "150px" }}>
            <strong>Expiry</strong>
          </TableCell>
          <TableCell sx={{ minWidth: "150px" }}>
            <strong>Select Options</strong>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {fields.map((field, index) => (
          <TableRow key={index}>
            <TableCell>{field.name}</TableCell>
            <TableCell>{field.type}</TableCell>
            <TableCell>{field.required}</TableCell>
            <TableCell>{field.maxLength}</TableCell>
            <TableCell>{field.expiry}</TableCell>
            <TableCell>{field.options}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
